import { v4 as uuidv4 } from 'uuid';
import store from '../slices';
import {refreshToken, requirePinCode, logout, stopImpersonation} from '../slices/auth.slice';
import {isImpersonating, isReverting} from '../services/impersonation';

const rolesWithoutPin = ['root', 'bac-user', 'bac-admin'];

const getDeviceId = () => {
    let deviceId = localStorage.getItem('deviceId');
    if (!deviceId) {
        deviceId = uuidv4();
        localStorage.setItem('deviceId', deviceId);
    }
    return deviceId;
};

const getDeviceName = () => {
    const userAgent = navigator.userAgent;
    const platform = navigator.platform;
    let deviceName = 'Unknown Device';
    if (userAgent.includes('Chrome')) deviceName = 'Chrome Browser';
    else if (userAgent.includes('Firefox')) deviceName = 'Firefox Browser';
    else if (userAgent.includes('Safari')) deviceName = 'Safari Browser';
    else if (userAgent.includes('Edge')) deviceName = 'Edge Browser';
    if (platform.includes('Win')) deviceName += ' (Windows)';
    else if (platform.includes('Mac')) deviceName += ' (macOS)';
    else if (platform.includes('Linux')) deviceName += ' (Linux)';
    return deviceName;
};

export const apiRequest = async (endpoint, method = 'GET', body = null) => {
    const isFormData = body instanceof FormData;

    const twoFactorVerified = localStorage.getItem('2fa-verified');
    const twoFactorTimestamp = localStorage.getItem('2fa-timestamp');

    const options = {
        method,
        headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            ...(!isFormData && { 'Content-Type': 'application/json' }),
            'x-device-id': getDeviceId(),
            'x-device-name': getDeviceName(),
            ...(twoFactorVerified && twoFactorTimestamp && {
                'x-2fa-verified': twoFactorVerified,
                'x-2fa-timestamp': twoFactorTimestamp
            })
        },
        body: method === 'POST' || method === 'PUT' ? (isFormData ? body : JSON.stringify(body)) : null,
    };

    const response = await fetch(`${process.env.REACT_APP_API_URL}${endpoint}`, options);

    // A deactivated session answers 401 without the Token-Expired header, which is
    // exactly how an ended impersonation shows up here — so impersonation is checked on
    // any 401, before the header-gated branch below. `isReverting` latches it: requests
    // that 401'd together must not fall through onto the restored admin token.
    if (response.status === 401 && (isImpersonating() || isReverting())) {
        const result = await store.dispatch(stopImpersonation({ reason: 'expired' }));
        window.location.assign(result.payload?.exitPath || '/');
        return;
    }

    if (response.status === 401 && response.headers.get('Token-Expired') === 'true') {
        const role = localStorage.getItem('role');
        const hasPinCode = localStorage.getItem('hasPinCode') === 'true';

        if (rolesWithoutPin.includes(role)) {
            try {
                await store.dispatch(refreshToken()).unwrap();
                return apiRequest(endpoint, method, body);
            } catch (refreshError) {
                store.dispatch(logout());
                window.location.href = '/login';
                return;
            }
        } else if (hasPinCode) {
            store.dispatch(requirePinCode());
            return;
        } else {
            // User has no PIN set, require full re-login
            store.dispatch(logout());
            window.location.href = '/login';
            return;
        }
    }

    if (response.ok) {
        return response.json();
    } else {
        let errorData = null;
        try {
            errorData = await response.json();
        } catch (e) {
            // Response body is not JSON or empty
            errorData = { error: response.statusText };
        }

        const error = new Error(errorData.error || response.statusText);
        error.response = {
            status: response.status,
            statusText: response.statusText,
            data: errorData
        };

        console.error('API request error:', error);
        throw error;
    }
};

export const imgUrl = url => {
    if (!url) return null;
    return `${process.env.REACT_APP_MEDIA_URL}${url}`;
}
